import { Paperclip, SendHorizontal, X } from "lucide-react";
import { forwardRef, useImperativeHandle, useRef, useState } from "react";

import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

interface MessageInputProps {
  onSend: (content: string, files: File[]) => void;
  disabled: boolean;
}

export interface MessageInputHandle {
  /** Append files from outside this component (e.g. a page-level drop zone). */
  addFiles: (files: File[]) => void;
}

const ACCEPT = "image/*,application/pdf";

function isAcceptedFile(file: File): boolean {
  return file.type.startsWith("image/") || file.type === "application/pdf";
}

/** Bottom composer: text area, attach-file button, file chips, and send. */
export const MessageInput = forwardRef<MessageInputHandle, MessageInputProps>(function MessageInput(
  { onSend, disabled },
  ref,
) {
  const [content, setContent] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const canSend = !disabled && (content.trim().length > 0 || files.length > 0);

  const addFiles = (incoming: File[]) => {
    const accepted = incoming.filter(isAcceptedFile);
    if (accepted.length === 0) {
      return;
    }
    setFiles((prev) => [...prev, ...accepted]);
  };

  useImperativeHandle(ref, () => ({ addFiles }));

  const handleAddFiles = (list: FileList | null) => {
    if (!list || list.length === 0) {
      return;
    }
    addFiles(Array.from(list));
    // Reset so selecting the same file again re-triggers onChange.
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleRemoveFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSend = () => {
    if (!canSend) {
      return;
    }
    onSend(content.trim(), files);
    setContent("");
    setFiles([]);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="border-border bg-card border-t p-3">
      {files.length > 0 && (
        <div className="mb-2 flex flex-wrap gap-2">
          {files.map((file, index) => (
            <div
              key={`${file.name}-${index}`}
              className="border-border bg-background flex max-w-[14rem] items-center gap-1.5 rounded-md border px-2 py-1 text-xs"
            >
              <Paperclip className="text-muted h-3 w-3 shrink-0" />
              <span className="truncate">{file.name}</span>
              <button
                type="button"
                onClick={() => handleRemoveFile(index)}
                className="text-muted hover:text-foreground shrink-0"
                aria-label={`Remove ${file.name}`}
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="flex items-end gap-2">
        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPT}
          multiple
          className="hidden"
          onChange={(event) => handleAddFiles(event.target.files)}
        />
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="shrink-0"
          disabled={disabled}
          onClick={() => fileInputRef.current?.click()}
          aria-label="Attach files"
        >
          <Paperclip className="h-4 w-4" />
        </Button>

        <Textarea
          value={content}
          onChange={(event) => setContent(event.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Describe the part you need…"
          rows={1}
          disabled={disabled}
          className="max-h-40 min-h-10 flex-1 resize-none"
        />

        <Button
          type="button"
          size="icon"
          className={cn("shrink-0")}
          disabled={!canSend}
          onClick={handleSend}
          aria-label="Send message"
        >
          <SendHorizontal className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
});
